import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSearchParams } from 'react-router-dom';
import { Translate, withLocalize } from 'react-localize-redux';
import { PageWrapper } from '../components/partials';
import Spinner from '../components/widgets/Spinner';
import Button from '../components/atoms/Button';
import { IconSizes } from '../components/foundations/Icon';
import GeneralPopup, { PopupTypes } from '../components/organisms/GeneralPopup';
import toaster from '../utils/toaster';
import { ensureINSToken, handleFailedRefresh } from '../services/interactiveServiceFetch';
import useAuthedChat from '../chatView/hooks/useAuthedChat';
import useSessionUrlSync from '../chatView/hooks/useSessionUrlSync';
import StreamMonitor from '../chatView/components/StreamMonitor';
import SessionNoticeToasts from '../chatView/components/SessionNoticeToasts';
import { EVENT_TYPES, SESSION_TYPES } from '../chatView/constants';
import useMonitors from './hooks/useMonitors';
import useWatchlistActivity from './hooks/useWatchlistActivity';
import { markAllRead, setMonitors as setAlertMonitors } from '../monitorAlerts/store';
import { countKey } from '../monitorAlerts/utils';
import { MonitorApiError, suggestMonitorTitle } from './service';
import MonitorComposer from './components/MonitorComposer';
import MonitorEditDialog from './components/MonitorEditDialog';
import AutoPausedDialog from './components/AutoPausedDialog';
import WatchlistEmptyPanel from './components/WatchlistEmptyPanel';
import MonitorCard from './components/MonitorCard';
import WatchlistFilters, { matchesFilter } from './components/WatchlistFilters';
import ThreadShelf from './components/ThreadShelf';
import {
    CARD_STATES,
    DEFAULT_SCHEDULE,
    MONITOR_STATUS,
    MONITOR_VERDICT_EVENT,
    RUN_ERROR_TOAST_KEYS,
    SLACK_AUTHORIZATION_NOT_COMPLETED,
    SLACK_CONNECTED_PARAM,
    SLACK_ERROR_PARAM,
    TOAST_AUTO_CLOSE_MS,
    TRANSLATION_KEY,
    WATCHLIST_EVENTS,
    WATCHLIST_FILTERS,
    buildMonitorSlackAuthUrl,
    trackWatchlistEvent,
} from './constants';
import { getCardState, getDisplayVerdict, presetToCron } from './utils';
import css from './page.css';

const emptyDraft = () => ({
    monitorId: null,
    title: '',
    prompt: '',
    cron: presetToCron(DEFAULT_SCHEDULE),
    slack: false,
});

const draftFromMonitor = monitor => ({
    monitorId: monitor.monitor_id,
    title: monitor.title ?? '',
    prompt: monitor.user_query,
    cron: monitor.schedule,
    slack: Boolean(monitor.slack_channels?.length),
});

const isDirty = (draft, initial) =>
    draft.title !== initial.title ||
    draft.prompt !== initial.prompt ||
    draft.cron !== initial.cron ||
    draft.slack !== initial.slack;

const Watchlist = ({ translate }) => {
    const [openMonitorId, setOpenMonitorId] = useState(null);
    const { chat, authLoading, authError } = useAuthedChat({
        sessionType: SESSION_TYPES.MONITOR,
        isViewingConversation: openMonitorId != null,
    });
    const authReady = !authLoading && !authError;
    const monitorsApi = useMonitors({ attachRun: chat.attachRun, enabled: authReady });
    const { monitors, loadError, liveState, canRestoreDefaults } = monitorsApi;
    useWatchlistActivity(authReady);

    useEffect(() => {
        if (!monitors) return;
        setAlertMonitors(monitors);
        markAllRead();
    }, [monitors]);

    const [composer, setComposer] = useState(null);
    const [composerInitial, setComposerInitial] = useState(null);
    // The composer holds on to the monitor it opened on, so a poll that drops that monitor
    // mid-edit still leaves Delete and the save-time 404 cleanup something to act on.
    const [composerMonitor, setComposerMonitor] = useState(null);
    const [composerSaving, setComposerSaving] = useState(false);
    const [composerError, setComposerError] = useState(null);
    const [discardIntent, setDiscardIntent] = useState(null);
    const [filter, setFilter] = useState(WATCHLIST_FILTERS.ALL);
    const [justAddedId, setJustAddedId] = useState(null);
    const [monitorToDelete, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [runningAll, setRunningAll] = useState(false);
    const [slackConnectingId, setSlackConnectingId] = useState(null);
    const [autoPauseDismissed, setAutoPauseDismissed] = useState(false);
    const [resumingAutoPaused, setResumingAutoPaused] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const autoPausedMonitors = useMemo(
        () => (monitors ?? []).filter(m => m.status === MONITOR_STATUS.AUTO_PAUSED),
        [monitors]
    );

    const live = useMemo(
        () => ({ ...liveState, streamingSessionIds: chat.streamingSessionIds }),
        [liveState, chat.streamingSessionIds]
    );

    const cardStates = useMemo(
        () => new Map((monitors ?? []).map(m => [m.monitor_id, getCardState(m, live)])),
        [monitors, live]
    );

    const showErrorToast = key =>
        toaster.error(translate(`${TRANSLATION_KEY}.TOASTS.${key}`), { autoClose: TOAST_AUTO_CLOSE_MS });

    useEffect(() => {
        const connectedChannel = searchParams.get(SLACK_CONNECTED_PARAM);
        const slackError = searchParams.get(SLACK_ERROR_PARAM);
        if (!connectedChannel && !slackError) return;
        if (connectedChannel) {
            trackWatchlistEvent(WATCHLIST_EVENTS.SLACK_CONNECTED, { channel: connectedChannel });
            toaster.success(translate(`${TRANSLATION_KEY}.TOASTS.SLACK_CONNECTED`, { channel: connectedChannel }),
                { autoClose: TOAST_AUTO_CLOSE_MS });
        } else if (slackError === SLACK_AUTHORIZATION_NOT_COMPLETED) {
            toaster.info(translate(`${TRANSLATION_KEY}.TOASTS.SLACK_CONNECT_NOT_COMPLETED`),
                { autoClose: TOAST_AUTO_CLOSE_MS });
        } else {
            showErrorToast('SLACK_CONNECT_FAILED');
        }
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            [SLACK_CONNECTED_PARAM, SLACK_ERROR_PARAM].forEach(param => next.delete(param));
            return next;
        }, { replace: true });
    }, [searchParams]);

    // The "Just added" badge marks a monitor only until its first result lands.
    useEffect(() => {
        if (!justAddedId) return;
        const added = monitors?.find(m => m.monitor_id === justAddedId);
        if (!added || added.latest_verdict) setJustAddedId(null);
    }, [monitors, justAddedId]);

    useEffect(() => {
        const clearConnectingOnRestore = event => {
            if (event.persisted) setSlackConnectingId(null);
        };
        window.addEventListener('pageshow', clearConnectingOnRestore);
        return () => window.removeEventListener('pageshow', clearConnectingOnRestore);
    }, []);

    const startSlackConnect = async monitorId => {
        trackWatchlistEvent(WATCHLIST_EVENTS.SLACK_CLICKED, { monitor_id: monitorId });
        setSlackConnectingId(monitorId);
        const refreshResult = await ensureINSToken();
        if (!refreshResult.ok) {
            setSlackConnectingId(null);
            showErrorToast('SLACK_CONNECT_FAILED');
            handleFailedRefresh(refreshResult);
            return false;
        }
        window.location.href = buildMonitorSlackAuthUrl(monitorId);
        return true;
    };

    const handleConnectSlack = monitor => startSlackConnect(monitor.monitor_id);

    const closeShelf = useCallback(() => setOpenMonitorId(null), []);

    const handleMonitorGone = useCallback(monitor => {
        monitorsApi.removeMonitorLocally(monitor.monitor_id);
        chat.purgeSessionState(monitor.session_id);
        setOpenMonitorId(current => (current === monitor.monitor_id ? null : current));
        showErrorToast('MONITOR_DELETED');
    }, [monitorsApi.removeMonitorLocally, chat.purgeSessionState]);

    const handleStreamEvent = useCallback((sessionId, runId, message) => {
        if (message.type === MONITOR_VERDICT_EVENT) monitorsApi.onCheckVerdict(sessionId, runId, message.data);
        else if (message.type === EVENT_TYPES.FINISH) monitorsApi.onCheckFinish(sessionId, runId, message.finishReason);
        chat.handleStreamMessage(sessionId, message);
    }, [monitorsApi.onCheckVerdict, monitorsApi.onCheckFinish, chat.handleStreamMessage]);

    const openThread = useCallback(monitor => {
        trackWatchlistEvent(WATCHLIST_EVENTS.OPEN_THREAD, { monitor_id: monitor.monitor_id });
        setOpenMonitorId(monitor.monitor_id);
        chat.loadSession(monitor.session_id);
    }, [chat.loadSession]);

    const openMonitor = monitors?.find(m => m.monitor_id === openMonitorId) ?? null;
    const editedMonitor = monitors?.find(m => m.monitor_id === composer?.monitorId) ?? composerMonitor;

    const openThreadFromUrl = useCallback(sessionId => {
        const monitor = monitors?.find(m => m.session_id === sessionId);
        if (monitor) openThread(monitor);
    }, [monitors, openThread]);

    useSessionUrlSync({
        ready: !authLoading && !authError && monitors != null,
        openSessionId: openMonitor?.session_id ?? null,
        onOpenFromUrl: openThreadFromUrl,
        onUrlCleared: closeShelf,
    });

    const handleRunNow = async monitor => {
        try {
            await monitorsApi.recheck(monitor);
        } catch (error) {
            if (!(error instanceof MonitorApiError)) return showErrorToast('GENERIC_ERROR');
            if (error.status === 404) return handleMonitorGone(monitor);
            if (error.status === 409 && RUN_ERROR_TOAST_KEYS[error.detail]) {
                return toaster.error(translate(RUN_ERROR_TOAST_KEYS[error.detail]), { autoClose: TOAST_AUTO_CLOSE_MS });
            }
            if (error.status === 502) return showErrorToast('RUN_FAILED');
            return showErrorToast('GENERIC_ERROR');
        }
    };

    const handleRunAll = async () => {
        if (runningAll) return;
        setRunningAll(true);
        try {
            await monitorsApi.recheckAllMonitors();
        } catch {
            showErrorToast('GENERIC_ERROR');
        } finally {
            setRunningAll(false);
        }
    };

    const handleRestoreDefaults = async () => {
        try {
            await monitorsApi.restoreDefaults();
        } catch {
            showErrorToast('GENERIC_ERROR');
        }
    };

    // The list the server returns no longer flags these monitors, which is what closes the dialog.
    const handleResumeAutoPaused = async monitorIds => {
        setResumingAutoPaused(true);
        try {
            await monitorsApi.resumeAutoPaused(monitorIds);
            const key = monitorIds.length ? countKey('AUTO_PAUSED_RESUMED', monitorIds.length) : 'AUTO_PAUSED_KEPT';
            toaster.success(translate(`${TRANSLATION_KEY}.TOASTS.${key}`, { count: monitorIds.length }),
                { autoClose: TOAST_AUTO_CLOSE_MS });
        } catch {
            showErrorToast('GENERIC_ERROR');
        } finally {
            setResumingAutoPaused(false);
        }
    };

    // `paused` comes from the card, which pauses itself for a reply before the poll reports it.
    const handlePauseToggle = async (monitor, paused) => {
        try {
            await monitorsApi.setPaused(monitor.monitor_id, paused);
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) return handleMonitorGone(monitor);
            if (error instanceof MonitorApiError && error.status === 409) return showErrorToast('UNPAUSE_IN_PROGRESS');
            showErrorToast('GENERIC_ERROR');
        }
    };

    // Returns whether the Slack state is settled: a monitor that is already gone leaves
    // nothing to retry, anything else failing is worth holding the composer open for.
    const handleDisconnectSlack = async monitor => {
        try {
            await monitorsApi.disconnectSlack(monitor.monitor_id);
            return true;
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) {
                handleMonitorGone(monitor);
                return true;
            }
            showErrorToast('GENERIC_ERROR');
            return false;
        }
    };

    const applyComposer = (monitor = null) => {
        setComposerError(null);
        const draft = monitor ? draftFromMonitor(monitor) : emptyDraft();
        setComposer(draft);
        setComposerInitial(draft);
        setComposerMonitor(monitor);
        if (monitor) closeShelf();
    };

    // An empty Watchlist has nothing to read, so the composer opens itself on the one thing
    // there is to do. Once only, so cancelling it is not undone on the next render.
    const composerAutoOpened = useRef(false);
    useEffect(() => {
        if (composerAutoOpened.current || composer != null) return;
        if (monitors == null || monitors.length > 0) return;
        composerAutoOpened.current = true;
        applyComposer();
    }, [monitors, composer]);

    // Re-opening the composer on what it already holds is a no-op, so an unsent draft
    // survives; switching it to another monitor asks before dropping unsaved work.
    const openComposer = (monitor = null) => {
        // A save in flight owns the composer; swapping the draft under it would let the
        // response close a replacement and report success for the monitor it replaced.
        if (composerSaving) return;
        if (composer && (monitor?.monitor_id ?? null) === composer.monitorId) return;
        if (composer && isDirty(composer, composerInitial)) {
            setDiscardIntent({ type: 'open', monitor });
            return;
        }
        applyComposer(monitor);
    };

    const closeComposer = () => {
        setComposer(null);
        setComposerInitial(null);
        setComposerMonitor(null);
        setComposerError(null);
    };

    const requestCloseComposer = () => {
        if (composerSaving) return;
        if (isDirty(composer, composerInitial)) setDiscardIntent({ type: 'close' });
        else closeComposer();
    };

    const handleComposerSave = async () => {
        const { monitorId, title, prompt, cron, slack } = composer;
        const userQuery = prompt.trim();
        if (!userQuery) return;
        const hadChannel = Boolean(editedMonitor?.slack_channels?.length);
        setComposerSaving(true);
        setComposerError(null);
        try {
            let savedId = monitorId;
            if (monitorId) {
                await monitorsApi.saveMonitor(monitorId, { user_query: userQuery, title: title.trim(), schedule: cron });
            } else {
                const created = await monitorsApi.addMonitor(userQuery, cron, title.trim());
                savedId = created.monitor_id;
                setJustAddedId(savedId);
            }
            // Slack is a redirect out of the page, so it can only run once the monitor exists.
            let slackSettled = true;
            if (slack && !hadChannel) slackSettled = await startSlackConnect(savedId);
            else if (!slack && hadChannel) slackSettled = await handleDisconnectSlack(editedMonitor);
            if (!slackSettled) {
                // The monitor is saved, so the draft is re-anchored to it: retrying the Slack
                // switch must edit that monitor rather than create a second one. The draft keeps
                // the destination the user asked for while the baseline holds what Slack really
                // has, so Save stays live for a one-click retry.
                const saved = { monitorId: savedId, title, prompt, cron, slack };
                setComposer(saved);
                setComposerInitial({ ...saved, slack: hadChannel });
                return;
            }
            closeComposer();
            toaster.success(
                translate(`${TRANSLATION_KEY}.TOASTS.${monitorId ? 'MONITOR_UPDATED' : 'MONITOR_ADDED'}`),
                { autoClose: TOAST_AUTO_CLOSE_MS }
            );
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 429) {
                setComposerError(translate(`${TRANSLATION_KEY}.COMPOSER.QUOTA_ERROR`));
            } else if (error instanceof MonitorApiError && error.status === 422 && error.detail) {
                setComposerError(error.detail);
            } else if (error instanceof MonitorApiError && error.status === 404 && editedMonitor) {
                handleMonitorGone(editedMonitor);
                closeComposer();
            } else {
                showErrorToast('GENERIC_ERROR');
            }
        } finally {
            setComposerSaving(false);
        }
    };

    const handleSaveTitle = async (monitor, title) => {
        try {
            await monitorsApi.saveMonitor(monitor.monitor_id, { title });
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) return handleMonitorGone(monitor);
            showErrorToast('GENERIC_ERROR');
            throw error;
        }
    };

    const handleSuggestTitle = async userQuery => {
        trackWatchlistEvent(WATCHLIST_EVENTS.SUGGEST_TITLE, { monitor_id: composer?.monitorId ?? null });
        try {
            const { title } = await suggestMonitorTitle(userQuery);
            if (!title) showErrorToast('GENERIC_ERROR');
            return title;
        } catch {
            showErrorToast('GENERIC_ERROR');
            return null;
        }
    };

    const handleDeleteConfirmed = async () => {
        setDeleting(true);
        try {
            await monitorsApi.removeMonitor(monitorToDelete);
            chat.purgeSessionState(monitorToDelete.session_id);
            setOpenMonitorId(current => (current === monitorToDelete.monitor_id ? null : current));
            if (composer?.monitorId === monitorToDelete.monitor_id) closeComposer();
        } catch {
            showErrorToast('GENERIC_ERROR');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    if (authLoading) return <Spinner show expanded />;

    const hasIdleMonitor = (monitors ?? []).some(m => m.status === MONITOR_STATUS.IDLE);

    // Editing happens in a dialog, so the monitor behind it stays listed and the board keeps
    // its place; only a brand new monitor is composed inline above the list.
    const listedMonitors = monitors ?? [];
    const shownMonitors = listedMonitors.filter(m => matchesFilter(m, filter, cardStates, liveState.liveRuns));
    const isEditing = composer?.monitorId != null;

    const composerProps = {
        draft: composer,
        slackChannelName: editedMonitor?.slack_channels?.[0]?.channel_name ?? null,
        saving: composerSaving,
        error: composerError,
        canSave: Boolean(composer?.prompt.trim()) && (!composer.monitorId || isDirty(composer, composerInitial)),
        onOpen: () => openComposer(),
        onChange: patch => setComposer(prev => ({ ...prev, ...patch })),
        onCancel: requestCloseComposer,
        onSave: handleComposerSave,
        onSuggestTitle: handleSuggestTitle,
    };

    const content = () => {
        if (authError || (loadError && monitors == null)) {
            return (
                <div className={css.errorState}>
                    <Translate id={`${TRANSLATION_KEY}.LOAD_ERROR`} />
                    {!authError && (
                        <Button type="secondary" onClick={monitorsApi.fetchNow}>
                            <Translate id={`${TRANSLATION_KEY}.RETRY`} />
                        </Button>
                    )}
                </div>
            );
        }
        if (monitors == null) {
            return (
                <div className={css.list}>
                    {[1, 2, 3].map(i => (
                        <div key={i} data-testid="monitor-skeleton" className={css.skeleton} />
                    ))}
                </div>
            );
        }
        return (
            <>
                {monitors.length > 0 && (
                    <WatchlistFilters
                        monitors={listedMonitors}
                        cardStates={cardStates}
                        liveRuns={liveState.liveRuns}
                        filter={filter}
                        shownCount={shownMonitors.length}
                        onFilterChange={next => {
                            trackWatchlistEvent(WATCHLIST_EVENTS.FILTER, { filter: next });
                            setFilter(next);
                        }}
                    />
                )}
                <MonitorComposer {...composerProps} draft={isEditing ? null : composer} />
                {monitors.length === 0 ? (
                    <WatchlistEmptyPanel
                        onRestore={handleRestoreDefaults}
                        restoring={monitorsApi.restoringDefaults}
                        canRestore={canRestoreDefaults}
                    />
                ) : (
                    <div className={css.list}>
                        {shownMonitors.map(monitor => (
                            <MonitorCard
                                key={monitor.monitor_id}
                                monitor={monitor}
                                cardState={cardStates.get(monitor.monitor_id)}
                                verdict={getDisplayVerdict(monitor, liveState.liveRuns[monitor.session_id])}
                                hasUpdate={chat.updatedSessionIds.includes(monitor.session_id)}
                                justAdded={monitor.monitor_id === justAddedId}
                                onOpenThread={() => openThread(monitor)}
                                onRunNow={() => handleRunNow(monitor)}
                                onOpenMonitorDialog={() => openComposer(monitor)}
                                onPauseToggle={paused => handlePauseToggle(monitor, paused)}
                                onRemove={() => setDeleteTarget(monitor)}
                                onConnectSlack={handleConnectSlack}
                                onDisconnectSlack={() => handleDisconnectSlack(monitor)}
                                slackConnecting={slackConnectingId === monitor.monitor_id}
                            />
                        ))}
                    </div>
                )}
            </>
        );
    };

    return (
        <PageWrapper className={css.page}>
            <div className={css.header}>
                <div className={css.headerText}>
                    <div className={css.headerTitle}>
                        <Translate id={`${TRANSLATION_KEY}.TITLE`} />
                        {monitors?.length > 0 && <span className={css.headerCount}>({monitors.length})</span>}
                    </div>
                    <div className={css.headerSubtitle}>
                        <Translate id={`${TRANSLATION_KEY}.SUBTITLE`} />
                    </div>
                </div>
                <div className={css.headerActions}>
                    <Button
                        type="flat"
                        className={css.runAll}
                        Icon="refresh"
                        iconSize={IconSizes.X_SMALL}
                        iconClass={classNames({ [css.spinning]: runningAll })}
                        disabled={runningAll || !hasIdleMonitor}
                        onClick={handleRunAll}
                    >
                        <Translate id={`${TRANSLATION_KEY}.RUN_ALL`} />
                    </Button>
                    <Button className={css.newMonitor} Icon="plus" iconSize={IconSizes.XX_SMALL} onClick={() => openComposer()}>
                        <Translate id={`${TRANSLATION_KEY}.NEW_MONITOR`} />
                    </Button>
                </div>
            </div>
            {content()}
            {chat.streamDescriptors.map(({ key, runId }) => (
                <StreamMonitor
                    key={runId}
                    sessionKey={key}
                    runId={runId}
                    onMessage={(sessionId, message) => handleStreamEvent(sessionId, runId, message)}
                    onError={(sessionId, errorMessage) => chat.handleStreamError(sessionId, errorMessage)}
                />
            ))}
            <MonitorEditDialog open={isEditing} {...composerProps} />
            <AutoPausedDialog
                open={autoPausedMonitors.length > 0 && !autoPauseDismissed}
                monitors={autoPausedMonitors}
                inactivityDays={monitorsApi.inactivityPauseDays}
                resuming={resumingAutoPaused}
                onResume={handleResumeAutoPaused}
                onKeepPaused={() => handleResumeAutoPaused([])}
                onClose={() => setAutoPauseDismissed(true)}
            />
            <SessionNoticeToasts chat={chat} onSessionGone={closeShelf} />
            <ThreadShelf
                open={openMonitor != null}
                monitor={openMonitor}
                verdict={openMonitor ? getDisplayVerdict(openMonitor, liveState.liveRuns[openMonitor.session_id]) : null}
                chat={chat}
                onClose={closeShelf}
                onSaveTitle={title => handleSaveTitle(openMonitor, title)}
                onOpenMonitorDialog={() => openComposer(openMonitor)}
            />
            <GeneralPopup
                open={discardIntent != null}
                type={PopupTypes.WARNING_WITH_CANCEL}
                title={`${TRANSLATION_KEY}.COMPOSER.${isEditing ? 'DISCARD_TITLE' : 'DISCARD_NEW_TITLE'}`}
                text={`${TRANSLATION_KEY}.COMPOSER.${isEditing ? 'DISCARD_TEXT' : 'DISCARD_NEW_TEXT'}`}
                acceptText={`${TRANSLATION_KEY}.COMPOSER.DISCARD_ACCEPT`}
                rejectText={`${TRANSLATION_KEY}.COMPOSER.DISCARD_REJECT`}
                onAccept={() => {
                    const intent = discardIntent;
                    setDiscardIntent(null);
                    if (intent?.type === 'open') applyComposer(intent.monitor);
                    else closeComposer();
                }}
                onReject={() => setDiscardIntent(null)}
            />
            <GeneralPopup
                open={monitorToDelete != null}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_KEY}.DELETE_MODAL.TITLE`}
                text={`${TRANSLATION_KEY}.DELETE_MODAL.MESSAGE`}
                acceptText={`${TRANSLATION_KEY}.DELETE_MODAL.ACCEPT`}
                rejectText={`${TRANSLATION_KEY}.DELETE_MODAL.REJECT`}
                onAccept={handleDeleteConfirmed}
                onReject={() => setDeleteTarget(null)}
                showSpinner={deleting}
            />
        </PageWrapper>
    );
};

Watchlist.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(Watchlist);
